import { apiClient } from './api';
import type { 
  SupplyOrder, 
  NewSupplyOrderRequest, 
  SupplyOrderFilters, 
  PaginationInfo,
  FollowUp 
} from '../types/supplies';
import type { Branch } from '../types/supplies';

export const suppliesService = {
  // Supply Orders
  createSupplyOrder: async (orderData: NewSupplyOrderRequest): Promise<SupplyOrder[]> => {
    const response = await apiClient.post<{ supplyOrders: SupplyOrder[]; pendingProducts: any[] }>('/supplies/orders', orderData);
    return response.data!.supplyOrders;
  },

  getSupplyOrders: async (filters: SupplyOrderFilters): Promise<{ orders: SupplyOrder[]; pagination: PaginationInfo }> => {
    const queryParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        if (key === 'is_viewed' && Array.isArray(value)) {
          // Handle is_viewed array parameter
          value.forEach(v => queryParams.append('is_viewed', v));
        } else {
          queryParams.append(key, String(value));
        }
      }
    });

    const response = await apiClient.get<{ orders: SupplyOrder[]; pagination: PaginationInfo }>(`/supplies/orders?${queryParams}`);
    return response.data!;
  },

  updateSupplyOrderStatus: async (orderId: number, status: 'pending' | 'done' | 'unavailable' | 'limited stock', supply_order_number?: number): Promise<SupplyOrder> => {
    const body: any = { status };
    if (status === 'done' && supply_order_number !== undefined) {
      body.supply_order_number = supply_order_number;
    }
    const response = await apiClient.patch<SupplyOrder>(`/supplies/orders/${orderId}/status`, body);
    return response.data!;
  },


  clearPhotosOnly: async (orderId: number): Promise<void> => {
    await apiClient.delete(`/supplies/orders/${orderId}/photos`);
  },

  addFollowUp: async (orderId: number, notes: string, photos?: string[], action?: string): Promise<FollowUp> => {
    const body: any = { notes };
    if (photos !== undefined) body.photos = photos;
    if (action !== undefined) body.action = action;
    const response = await apiClient.post<FollowUp>(`/supplies/orders/${orderId}/follow-up`, body);
    return response.data!;
  },
  updateFollowUp: async (orderId: number, followUpId: number | string, notes?: string, photos?: string[], action?: string): Promise<FollowUp> => {
    const body: any = {};
    if (notes !== undefined) body.notes = notes;
    if (photos !== undefined) body.photos = photos;
    if (action !== undefined) body.action = action;
    const response = await apiClient.patch<FollowUp>(`/supplies/orders/${orderId}/follow-up/${followUpId}`, body);
    return response.data!;
  },

  updateNotes: async (orderId: number, notes: string): Promise<void> => {
    await apiClient.patch(`/supplies/orders/${orderId}/notes`, { notes });
  },

  updateAssignment: async (orderId: number, assignedTo: number | null): Promise<void> => {
    await apiClient.patch(`/supplies/orders/${orderId}/assignment`, { assigned_to: assignedTo });
  },
  
  updateSupplier: async (orderId: number, supplier: string): Promise<void> => {
    await apiClient.patch(`/supplies/orders/${orderId}/supplier`, { supplier });
  },

  uploadPhotos: async (orderId: number, files: File[]): Promise<string[]> => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('photos', file);
    });

    const response = await apiClient.uploadFiles<{ photos: string[] }>(`/supplies/orders/${orderId}/photos`, formData);
    return response.data!.photos;
  },

  deleteSupplyOrder: async (orderId: number): Promise<void> => {
    await apiClient.delete(`/supplies/orders/${orderId}`);
  },
  
    // Mark Notes, Photos, or Follow Up as viewed
    updateIsViewed: async (orderId: number, column: 'notes' | 'photos' | 'follow_up'): Promise<string | undefined> => {
      const response = await apiClient.patch<{ is_viewed?: string }>(`/supplies/orders/${orderId}/viewed`, { column });
      return response.data && response.data.is_viewed ? response.data.is_viewed : undefined;
    },

    // Mark Notes, Photos, or Follow Up as viewed for PendingProduct
    updatePendingIsViewed: async (pendingId: number, section: 'notes' | 'photos' | 'follow_up'): Promise<string | undefined> => {
      const response = await apiClient.patch<{ is_viewed?: string }>(`/supplies/pending-products/${pendingId}/viewed`, { section });
      return response.data && response.data.is_viewed ? response.data.is_viewed : undefined;
    },

  // Branches
  getBranches: async (): Promise<Branch[]> => {
    const response = await apiClient.get<Branch[]>('/branches');
    return response.data!;
  }
};
